import type {
  LineSourcingComparison,
  SourcingAlternative,
  SourcingComparisonOutcome,
  SourcingComparisonRequest,
  SourcingPlatform,
  SourcingSellerType,
  SourcingStockStatus,
  UpdateLineSourcingDetailRequest,
} from '@/infrastructure/FulfillmentAdminClient';

/**
 * The line sourcing detail editor's draft, checks and wording (SSD-7177). The checks mirror Facade core
 * `LineSourcingDetailRules` so the operator hears about a broken rule beside the box that breaks it; the
 * server stays the authority and its refusal is still shown when the two disagree.
 */

/** Most options one comparison records (core `SOURCING_ALTERNATIVES_MAX`). */
export const SOURCING_ALTERNATIVES_MAX = 10;

/** The request DTO's caps: a larger figure is a typo, not a price or a quantity. */
const KRW_MAX = 100_000_000;
const QUANTITY_MAX = 10_000;

export const SOURCING_PLATFORMS: SourcingPlatform[] = [
  'COUPANG',
  'NAVER',
  'OLIVE_YOUNG',
  'BRAND_OFFICIAL_SITE',
  'OTHER_RETAILER',
];

export const SOURCING_PLATFORM_LABELS: Record<SourcingPlatform, string> = {
  COUPANG: 'Coupang',
  NAVER: 'Naver',
  OLIVE_YOUNG: 'Olive Young',
  BRAND_OFFICIAL_SITE: 'Brand official site',
  OTHER_RETAILER: 'Other retailer',
};

/** The seller classes each platform can have (core `SELLER_TYPES_BY_PLATFORM`); UNKNOWN fits every platform. */
export const SELLER_TYPES_BY_PLATFORM: Record<SourcingPlatform, SourcingSellerType[]> = {
  COUPANG: ['COUPANG_ROCKET', 'COUPANG_ROCKET_GROWTH', 'COUPANG_SELLER_DELIVERY'],
  NAVER: ['NAVER_BRAND_OFFICIAL_STORE', 'NAVER_GENERAL_SELLER'],
  OLIVE_YOUNG: ['OLIVE_YOUNG'],
  BRAND_OFFICIAL_SITE: ['BRAND_OFFICIAL_SITE'],
  OTHER_RETAILER: ['OTHER_RETAILER'],
};

export const SELLER_TYPE_LABELS: Record<SourcingSellerType, string> = {
  COUPANG_ROCKET: 'Coupang 로켓배송',
  COUPANG_ROCKET_GROWTH: 'Coupang 판매자로켓',
  COUPANG_SELLER_DELIVERY: 'Coupang 일반 판매자',
  NAVER_BRAND_OFFICIAL_STORE: 'Naver brand official store',
  NAVER_GENERAL_SELLER: 'Naver general seller',
  OLIVE_YOUNG: 'Olive Young',
  BRAND_OFFICIAL_SITE: 'Brand official site',
  OTHER_RETAILER: 'Other retailer',
  UNKNOWN: 'Unknown (could not tell)',
};

/** Labels for a choice already listed under its platform, where the platform name would repeat. */
export const SELLER_TYPE_OPTION_LABELS: Record<SourcingSellerType, string> = {
  ...SELLER_TYPE_LABELS,
  COUPANG_ROCKET: '로켓배송',
  COUPANG_ROCKET_GROWTH: '판매자로켓',
  COUPANG_SELLER_DELIVERY: '일반 판매자',
};

export const SOURCING_STOCK_STATUSES: SourcingStockStatus[] = [
  'IN_STOCK',
  'LIMITED',
  'OUT_OF_STOCK',
  'NOT_LISTED',
  'UNKNOWN',
];

export const STOCK_STATUS_LABELS: Record<SourcingStockStatus, string> = {
  IN_STOCK: 'In stock',
  LIMITED: 'Limited',
  OUT_OF_STOCK: 'Out of stock',
  NOT_LISTED: 'Not listed',
  UNKNOWN: 'Unknown',
};

export const OUTCOME_LABELS: Record<SourcingComparisonOutcome, string> = {
  COMPARED: 'Compared',
  NO_ALTERNATIVE_AVAILABLE: 'No alternative available',
  NOT_COMPARED: 'Not compared',
};

/** Only these can be bought; the chosen option must be one of them. */
const BUYABLE_STOCK = new Set<SourcingStockStatus>(['IN_STOCK', 'LIMITED']);

export const sellerTypeLabel = (sellerType: SourcingSellerType | null | undefined): string =>
  sellerType ? (SELLER_TYPE_LABELS[sellerType] ?? sellerType) : '—';

/** Whether a seller type can sell on the platform; UNKNOWN is accepted everywhere. */
export const sellsOn = (platform: SourcingPlatform, sellerType: SourcingSellerType): boolean =>
  sellerType === 'UNKNOWN' || SELLER_TYPES_BY_PLATFORM[platform].includes(sellerType);

/** One option row, held as text so a half-typed number is not a value yet. */
export interface SourcingAlternativeDraft {
  alternativeId: string;
  platform: SourcingPlatform | '';
  sellerType: SourcingSellerType | '';
  quantityBasis: string;
  itemsTotalKrw: string;
  deliveryFeeKrw: string;
  stockStatus: SourcingStockStatus | '';
  expectedArrivalDate: string;
  quantityLimit: string;
  /** Recorded fields this form does not edit (unit price, seller name, link, note), sent back as they were. */
  kept: Pick<SourcingAlternative, 'unitPriceKrw' | 'sellerName' | 'url' | 'note'>;
}

/** `''` means no comparison is recorded: saving it clears the stored one. */
export type SourcingOutcomeChoice = SourcingComparisonOutcome | '';

export interface SourcingDetailDraft {
  sellerType: SourcingSellerType | '';
  outcome: SourcingOutcomeChoice;
  requiredArrivalDate: string;
  chosenAlternativeId: string;
  alternatives: SourcingAlternativeDraft[];
}

/** What a line carries that the editor reads. */
export interface SourcingDetailLine {
  totalQuantity: number;
  sellerType?: SourcingSellerType | null;
  sourcingComparison?: LineSourcingComparison | null;
}

const textOf = (value: number | string | undefined): string => (value === undefined ? '' : String(value));

const keptOf = (alternative: SourcingAlternative): SourcingAlternativeDraft['kept'] => {
  const kept: SourcingAlternativeDraft['kept'] = {};
  if (alternative.unitPriceKrw !== undefined) {
    kept.unitPriceKrw = alternative.unitPriceKrw;
  }
  if (alternative.sellerName !== undefined) {
    kept.sellerName = alternative.sellerName;
  }
  if (alternative.url !== undefined) {
    kept.url = alternative.url;
  }
  if (alternative.note !== undefined) {
    kept.note = alternative.note;
  }
  return kept;
};

/** A new option row priced, by default, for the whole line. */
export const blankAlternativeDraft = (alternativeId: string, totalQuantity: number): SourcingAlternativeDraft => ({
  alternativeId,
  platform: '',
  sellerType: '',
  quantityBasis: String(totalQuantity),
  itemsTotalKrw: '',
  deliveryFeeKrw: '',
  stockStatus: '',
  expectedArrivalDate: '',
  quantityLimit: '',
  kept: {},
});

/** The form as the stored detail fills it. */
export const sourcingDraftOf = (line: SourcingDetailLine): SourcingDetailDraft => {
  const comparison = line.sourcingComparison ?? null;
  return {
    sellerType: line.sellerType ?? '',
    outcome: comparison?.outcome ?? '',
    requiredArrivalDate: comparison?.requiredArrivalDate ?? '',
    chosenAlternativeId: comparison?.chosenAlternativeId ?? '',
    alternatives: (comparison?.alternatives ?? []).map((alternative) => ({
      alternativeId: alternative.alternativeId,
      platform: alternative.platform,
      sellerType: alternative.sellerType ?? '',
      quantityBasis: textOf(alternative.quantityBasis),
      itemsTotalKrw: textOf(alternative.itemsTotalKrw),
      deliveryFeeKrw: textOf(alternative.deliveryFeeKrw),
      stockStatus: alternative.stockStatus,
      expectedArrivalDate: alternative.expectedArrivalDate ?? '',
      quantityLimit: textOf(alternative.quantityLimit),
      kept: keptOf(alternative),
    })),
  };
};

const CALENDAR_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** A day that exists: the shape alone would let 2026-02-31 through. */
const isCalendarDate = (text: string): boolean => {
  const parts = CALENDAR_DATE.exec(text);
  if (!parts) {
    return false;
  }
  const [year, month, day] = [Number(parts[1]), Number(parts[2]), Number(parts[3])];
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
};

/** A whole number within bounds, or null when the text is not one. */
const wholeNumber = (text: string, min: number, max: number): number | null => {
  const trimmed = text.trim();
  if (!/^\d+$/.test(trimmed)) {
    return null;
  }
  const value = Number(trimmed);
  return value >= min && value <= max ? value : null;
};

/** The landed total the comparison is decided on, or null until both parts are whole numbers. */
export const landedTotalOf = (
  alternative: Pick<SourcingAlternativeDraft, 'itemsTotalKrw' | 'deliveryFeeKrw'>,
): number | null => {
  const items = wholeNumber(alternative.itemsTotalKrw, 0, KRW_MAX);
  const fee = wholeNumber(alternative.deliveryFeeKrw, 0, KRW_MAX);
  return items === null || fee === null ? null : items + fee;
};

export type SourcingAlternativeField =
  | 'platform'
  | 'sellerType'
  | 'quantityBasis'
  | 'itemsTotalKrw'
  | 'deliveryFeeKrw'
  | 'stockStatus'
  | 'expectedArrivalDate'
  | 'quantityLimit';

/** The sentence standing against each box that has one, and the form-level ones that name no single box. */
export interface SourcingDraftErrors {
  sellerType?: string;
  outcome?: string;
  requiredArrivalDate?: string;
  chosen?: string;
  alternatives: Record<string, Partial<Record<SourcingAlternativeField, string>>>;
  form: string[];
}

export const hasSourcingDraftErrors = (errors: SourcingDraftErrors): boolean =>
  errors.sellerType !== undefined ||
  errors.outcome !== undefined ||
  errors.requiredArrivalDate !== undefined ||
  errors.chosen !== undefined ||
  errors.form.length > 0 ||
  Object.values(errors.alternatives).some((fields) => Object.keys(fields).length > 0);

/** Row numbers as the operator sees them: the first option is "Option 1". */
const optionName = (draft: SourcingDetailDraft, alternativeId: string): string =>
  `Option ${draft.alternatives.findIndex((alternative) => alternative.alternativeId === alternativeId) + 1}`;

/**
 * Whether an option could have been bought instead (core `isFeasible`): buyable stock, a price, enough pieces
 * under its limit, and a promised arrival on or before the deadline.
 */
const isFeasible = (
  alternative: SourcingAlternativeDraft,
  requiredArrivalDate: string,
  totalQuantity: number,
): boolean => {
  const limit =
    alternative.quantityLimit.trim() === '' ? null : wholeNumber(alternative.quantityLimit, 1, QUANTITY_MAX);
  return (
    alternative.stockStatus !== '' &&
    BUYABLE_STOCK.has(alternative.stockStatus) &&
    landedTotalOf(alternative) !== null &&
    (limit === null || limit >= totalQuantity) &&
    alternative.expectedArrivalDate !== '' &&
    alternative.expectedArrivalDate <= requiredArrivalDate
  );
};

const alternativeErrorsOf = (
  alternative: SourcingAlternativeDraft,
): Partial<Record<SourcingAlternativeField, string>> => {
  const errors: Partial<Record<SourcingAlternativeField, string>> = {};
  if (alternative.platform === '') {
    errors.platform = 'Pick the platform.';
  } else if (alternative.sellerType !== '' && !sellsOn(alternative.platform, alternative.sellerType)) {
    errors.sellerType = `Not a ${SOURCING_PLATFORM_LABELS[alternative.platform]} seller type.`;
  }
  if (wholeNumber(alternative.quantityBasis, 1, QUANTITY_MAX) === null) {
    errors.quantityBasis = `Pieces priced: a whole number from 1 to ${QUANTITY_MAX.toLocaleString('en-US')}.`;
  }
  const items = alternative.itemsTotalKrw.trim();
  const fee = alternative.deliveryFeeKrw.trim();
  if (items !== '' && wholeNumber(items, 0, KRW_MAX) === null) {
    errors.itemsTotalKrw = 'Whole KRW, 0 or more.';
  }
  if (fee !== '' && wholeNumber(fee, 0, KRW_MAX) === null) {
    errors.deliveryFeeKrw = 'Whole KRW, 0 or more (0 when free).';
  }
  if (items !== '' && fee === '' && errors.itemsTotalKrw === undefined) {
    errors.deliveryFeeKrw = 'Enter the delivery fee too (0 when free).';
  }
  if (fee !== '' && items === '' && errors.deliveryFeeKrw === undefined) {
    errors.itemsTotalKrw = 'Enter the items total too.';
  }
  if (alternative.stockStatus === '') {
    errors.stockStatus = 'Pick the stock state.';
  } else if (BUYABLE_STOCK.has(alternative.stockStatus) && items === '' && fee === '') {
    errors.itemsTotalKrw = `${STOCK_STATUS_LABELS[alternative.stockStatus]} needs its price.`;
  }
  if (alternative.expectedArrivalDate !== '' && !isCalendarDate(alternative.expectedArrivalDate)) {
    errors.expectedArrivalDate = 'A date, such as 2026-10-08.';
  }
  if (alternative.quantityLimit.trim() !== '' && wholeNumber(alternative.quantityLimit, 1, QUANTITY_MAX) === null) {
    errors.quantityLimit = 'A whole number of pieces, 1 or more; blank when there is no limit.';
  }
  return errors;
};

/** Every rule the server would refuse the draft on, said against the box that breaks it. */
export const sourcingDraftErrorsOf = (draft: SourcingDetailDraft, totalQuantity: number): SourcingDraftErrors => {
  const errors: SourcingDraftErrors = { alternatives: {}, form: [] };
  if (draft.outcome === '') {
    // A seller type alone, or nothing at all, is always a valid save.
    return errors;
  }
  const { alternatives } = draft;
  if (alternatives.length > SOURCING_ALTERNATIVES_MAX) {
    errors.form.push(`A comparison records at most ${SOURCING_ALTERNATIVES_MAX} options.`);
  }
  for (const alternative of alternatives) {
    const fields = alternativeErrorsOf(alternative);
    if (Object.keys(fields).length > 0) {
      errors.alternatives[alternative.alternativeId] = fields;
    }
  }

  const hasChosen = draft.chosenAlternativeId !== '';
  switch (draft.outcome) {
    case 'COMPARED':
      if (alternatives.length < 2) {
        errors.outcome = 'Compared records the option bought and at least one other.';
      }
      break;
    case 'NO_ALTERNATIVE_AVAILABLE':
      if (alternatives.length < 1) {
        errors.outcome = 'No alternative available still records the option that was bought.';
      }
      break;
    case 'NOT_COMPARED':
      if (alternatives.length > 1) {
        errors.outcome = 'Not compared records at most the option that was bought.';
      } else if (alternatives.length === 1 && !hasChosen) {
        errors.chosen = 'Mark the one option as the one bought.';
      }
      break;
  }
  if (draft.outcome !== 'NOT_COMPARED') {
    if (!hasChosen && alternatives.length > 0) {
      errors.chosen = 'Mark the option that was bought.';
    }
    if (draft.requiredArrivalDate === '') {
      errors.requiredArrivalDate = 'Needed to judge which options could arrive in time.';
    } else if (!isCalendarDate(draft.requiredArrivalDate)) {
      errors.requiredArrivalDate = 'A date, such as 2026-10-08.';
    }
  }

  const chosen = alternatives.find((alternative) => alternative.alternativeId === draft.chosenAlternativeId);
  if (!chosen) {
    return errors;
  }
  const chosenFields = errors.alternatives[chosen.alternativeId] ?? {};
  if (chosen.stockStatus !== '' && !BUYABLE_STOCK.has(chosen.stockStatus)) {
    chosenFields.stockStatus = 'The option bought must have been in stock or limited.';
  }
  if (draft.sellerType === '') {
    errors.sellerType = 'Pick the seller type the line was bought from (Unknown when it cannot be told).';
  } else if (chosen.platform !== '' && !sellsOn(chosen.platform, draft.sellerType)) {
    errors.sellerType = `${SELLER_TYPE_LABELS[draft.sellerType]} is not a ${SOURCING_PLATFORM_LABELS[chosen.platform]} seller, but the option bought is on ${SOURCING_PLATFORM_LABELS[chosen.platform]}.`;
  } else if (chosen.sellerType !== '' && chosen.sellerType !== draft.sellerType) {
    errors.sellerType = `The option bought says ${SELLER_TYPE_LABELS[chosen.sellerType]}; the line seller type must match it.`;
  }
  if (Object.keys(chosenFields).length > 0) {
    errors.alternatives[chosen.alternativeId] = chosenFields;
  }
  if (draft.outcome === 'NO_ALTERNATIVE_AVAILABLE' && isCalendarDate(draft.requiredArrivalDate)) {
    const feasible = alternatives.find(
      (alternative) => alternative !== chosen && isFeasible(alternative, draft.requiredArrivalDate, totalQuantity),
    );
    if (feasible) {
      errors.outcome = `${optionName(draft, feasible.alternativeId)} could have been bought in time, so the outcome is Compared.`;
    }
  }
  return errors;
};

const numberOf = (text: string): number | undefined => (text.trim() === '' ? undefined : Number(text.trim()));

const alternativeRequestOf = (alternative: SourcingAlternativeDraft): SourcingAlternative => {
  const itemsTotalKrw = numberOf(alternative.itemsTotalKrw);
  const deliveryFeeKrw = numberOf(alternative.deliveryFeeKrw);
  const landed = landedTotalOf(alternative);
  const quantityLimit = numberOf(alternative.quantityLimit);
  return {
    ...alternative.kept,
    alternativeId: alternative.alternativeId,
    platform: alternative.platform as SourcingPlatform,
    ...(alternative.sellerType !== '' && { sellerType: alternative.sellerType }),
    quantityBasis: Number(alternative.quantityBasis.trim()),
    ...(itemsTotalKrw !== undefined && { itemsTotalKrw }),
    ...(deliveryFeeKrw !== undefined && { deliveryFeeKrw }),
    // Derived, never typed: the rule refuses a landed total that is not items + fee.
    ...(landed !== null && { landedTotalKrw: landed }),
    stockStatus: alternative.stockStatus as SourcingStockStatus,
    ...(alternative.expectedArrivalDate !== '' && { expectedArrivalDate: alternative.expectedArrivalDate }),
    ...(quantityLimit !== undefined && { quantityLimit }),
  };
};

/** The comparison part of the request; null clears the stored one. */
const comparisonRequestOf = (draft: SourcingDetailDraft): SourcingComparisonRequest | null => {
  if (draft.outcome === '') {
    return null;
  }
  return {
    outcome: draft.outcome,
    ...(draft.requiredArrivalDate !== '' && { requiredArrivalDate: draft.requiredArrivalDate }),
    ...(draft.chosenAlternativeId !== '' && { chosenAlternativeId: draft.chosenAlternativeId }),
    alternatives: draft.alternatives.map(alternativeRequestOf),
  };
};

/**
 * The whole detail as the screen holds it. The dialog sends only the parts that differ from what it opened on, and
 * Order Detail passes no `expectedUpdatedAt`, so the last save wins (SSD-7665); a caller that passes one asks the
 * server to refuse a line that moved since.
 */
export const sourcingRequestOf = (
  draft: SourcingDetailDraft,
  expectedUpdatedAt: string | null | undefined,
): UpdateLineSourcingDetailRequest => ({
  sellerType: draft.sellerType === '' ? null : draft.sellerType,
  sourcingComparison: comparisonRequestOf(draft),
  ...(expectedUpdatedAt ? { expectedUpdatedAt } : {}),
});

/** Whether the line has anything recorded, so the row knows whether to show it. */
export const hasSourcingDetail = (line: SourcingDetailLine): boolean =>
  Boolean(line.sellerType) || Boolean(line.sourcingComparison);

/** One short phrase for the line row: the seller type, then the outcome with its option count. */
export const sourcingSummaryOf = (line: SourcingDetailLine): string => {
  const parts: string[] = [];
  if (line.sellerType) {
    parts.push(sellerTypeLabel(line.sellerType));
  }
  const comparison = line.sourcingComparison;
  if (comparison) {
    const count = comparison.alternatives.length;
    parts.push(
      `${OUTCOME_LABELS[comparison.outcome] ?? comparison.outcome} (${count} option${count === 1 ? '' : 's'})`,
    );
  }
  return parts.join(' · ');
};

/** The line's purchase platform and its seller type must name the same platform (SSD-7396). */
export const PLATFORM_MISMATCH_RULE = 'PLATFORM_MISMATCH';
export const PLATFORM_MISMATCH_MESSAGE =
  "The seller type belongs to another platform than the line's purchase platform.";

/** Rule codes the server names, worded for the operator; an unlisted rule keeps the server's own sentence. */
const SOURCING_RULE_MESSAGES: Record<string, string> = {
  ROUTE_NOT_PURCHASED: 'Sourcing detail is recorded on JIT lines only.',
  TOO_MANY_ALTERNATIVES: `A comparison records at most ${SOURCING_ALTERNATIVES_MAX} options.`,
  COMPARED_NEEDS_TWO: 'Compared records the option bought and at least one other.',
  NO_ALTERNATIVE_NEEDS_CHOSEN: 'No alternative available still records the option that was bought.',
  NOT_COMPARED_HAS_ALTERNATIVES: 'Not compared records at most the option that was bought.',
  CHOSEN_MISSING: 'Mark the option that was bought.',
  REQUIRED_ARRIVAL_DATE_MISSING: 'Enter the required arrival date.',
  SELLER_TYPE_MISSING: 'Pick the line seller type (Unknown when it cannot be told).',
  PLATFORM_MISMATCH: PLATFORM_MISMATCH_MESSAGE,
};

interface SourcingRefusalBody {
  code?: string;
  rule?: string;
  message?: string | string[];
}

/** The 400 refusal as one sentence, keyed off its rule when the server named one. */
export const describeSourcingRefusal = (body: SourcingRefusalBody | undefined): string | undefined => {
  if (!body) {
    return undefined;
  }
  const message = Array.isArray(body.message) ? body.message.join(' · ') : body.message;
  const worded = body.rule ? SOURCING_RULE_MESSAGES[body.rule] : undefined;
  if (worded) {
    return message ? `${worded} (${message})` : worded;
  }
  return message || undefined;
};
